import { useFirestore } from "reactfire";
import { doc, collection, getDocs, query, setDoc, deleteDoc } from "firebase/firestore";
import { useEffect, useState } from "react";

interface UseFirestoreCollectionReadWriteOptions {
	idField?: string;
}

export const useFirestoreCollectionReadWrite = <T extends { [key: string]: any }>(
	collectionPath: string,
	options?: UseFirestoreCollectionReadWriteOptions,
) => {
	const firestore = useFirestore();

	const [objects, setObjects] = useState<T[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	const fetchData = async () => {
		setIsLoading(true);
		const col = collection(firestore, collectionPath);
		const querySnapshot = await getDocs(query(col));
		const _objects = querySnapshot.docs.map((docSnap) => {
			const data = docSnap.data() as T;
			return options?.idField ? { ...data, [options.idField]: docSnap.id } : data;
		});
		setObjects(_objects);
		setIsLoading(false);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const saveObject = async (id: string, object: T) => {
		setIsUpdating(true);
		try {
			const col = collection(firestore, collectionPath);
			const docRef = doc(col, id);
			await setDoc(docRef, object, { merge: false });
			await fetchData();
		} finally {
			setIsUpdating(false);
		}
	};

	const deleteObject = async (id: string) => {
		const rulesCollection = collection(firestore, collectionPath);
		const docRef = doc(rulesCollection, id);
		await deleteDoc(docRef);
		await fetchData();
	};

	return { objects, isLoading, isUpdating, saveObject, deleteObject };
};
